import { type RefObject, type ReactNode } from "react";

import { Button as AriaButton, type ButtonProps as AriaButtonProps } from "react-aria-components";

import { CircularProgress } from "#components/CircularProgress";
import { buttonDefaultVariants, buttonVariants } from "#components/shared/buttonVariants";
import { usePendingState } from "#hooks";
import { customTv } from "#utils";

import type { VariantProps } from "tailwind-variants";

const buttonStyles = customTv({
  base: "relative px-md py-xxs transition-all ease-in-out font-semibold rounded-md text-label-medium flex items-center gap-x-xs text-interactive-primary-button-enabled-foreground outline-none border border-transparent disabled:cursor-not-allowed",
  variants: buttonVariants,
  defaultVariants: buttonDefaultVariants,
});

export interface ButtonProps
  extends Omit<AriaButtonProps, "className">, VariantProps<typeof buttonStyles> {
  className?: string;
  /** When true, shows loading spinner and disables the button */
  isPending?: boolean;
  /** Text to show alongside the spinner when isPending is true */
  pendingText?: string;
  IconLeft?: ReactNode;
  IconRight?: ReactNode;
  ref?: RefObject<HTMLButtonElement | null>;
}

export const Button = (props: ButtonProps) => {
  const {
    variant,
    children,
    IconLeft,
    IconRight,
    ref,
    isPending = false,
    pendingText = "Loading…",
    ...restProps
  } = props;

  const shouldUseInvertedProgress = (variant ?? "primary") === "primary";
  const { isProgressVisible } = usePendingState(isPending);

  return (
    <AriaButton
      {...restProps}
      className={buttonStyles({
        variant,
        class: props.className,
      })}
      isPending={isPending}
      ref={ref}
    >
      {(renderProps) => {
        if (typeof children === "function") {
          return children(renderProps);
        }

        const spinner = (
          <CircularProgress
            size="small"
            variant={shouldUseInvertedProgress ? "inverted" : "default"}
          />
        );

        if (isProgressVisible) {
          return (
            <>
              {/*
               * Invisible in-flow placeholder sized to the loading content.
               * Ensures the button is wide enough for the spinner + pendingText
               * without collapsing to the width of the hidden children.
               */}
              <span className="invisible inline-flex items-center gap-x-xs">
                {spinner}
                <span className="text-label-medium font-semibold">{pendingText}</span>
              </span>
              <span className="absolute inset-0 flex items-center justify-center gap-x-xs">
                {spinner}
                <span className="text-label-medium font-semibold">{pendingText}</span>
              </span>
            </>
          );
        }

        return (
          <>
            {IconLeft ? (
              <span className="relative flex items-center justify-center">{IconLeft}</span>
            ) : null}
            {children != null ? (
              IconLeft != null || IconRight != null ? (
                <span className="inline-flex min-w-0 items-center gap-x-xs">{children}</span>
              ) : (
                children
              )
            ) : null}
            {IconRight ? <span>{IconRight}</span> : null}
          </>
        );
      }}
    </AriaButton>
  );
};
